<template>
  <div>
     <AlertLayout title="Reminders!"
      message="Kindly note that the <span style='color: red;'>Pending</span> status means the assessment is yet to be done.<br>Kindly note that the <span style='color: orange;'>Ongoing</span> status means the assessment has been attempted but failed.<br>Kindly note that the <span style='color: green;'>Completed</span> status means the assessment has been done and completed.<br>Kindly note that once you start a test, the timer starts running for the given duration of time indicated.<br>Kindly note that once the duration elapses, your assessment will be automatically submitted."
      type="warning" />

    <div class="flex flex-col items-center justify-center px-10">
      <div class="rounded-lg w-full py-6">
        <h1 class="text-2xl font-bold mb-4">{{ assessmentName }}</h1>
        <div class="grid grid-cols-2 gap-4">
          <div class="border-b-2">
            <p class="font-bold py-1">Test Name:</p>
            <p class="py-2">{{ assessment.assessment_name }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Status:</p>
            <p class="py-2">
              <span :class="getStatusClass()" class="px-3 py-1 rounded-full text-sm font-medium">
                {{ getUserAssessmentStatus() }}
              </span>
            </p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Deadline:</p>
            <p class="py-2">{{ new Date(assessment.deadline).toLocaleString() }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Date Created:</p>
            <p class="py-2">{{ new Date(assessment.created_at).toLocaleString() }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Duration:</p>
            <p class="py-2">{{ formatDuration(assessment.duration) }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Number of questions:</p>
            <p class="py-2">{{ assessment.number_of_questions }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Pass Mark:</p>
            <p class="py-2">{{ assessment.passmark || 50 }}%</p>
          </div>
          <div class="border-b-2" v-if="assessmentResult && assessmentResult.score !== null">
            <p class="font-bold py-1">Your Score:</p>
            <p class="py-2">
              <span :class="isPassed ? 'text-green-600 font-bold' : 'text-red-600 font-bold'">
                {{ assessmentResult.score }}/{{ assessment.number_of_questions }} 
                ({{ ((assessmentResult.score / assessment.number_of_questions) * 100).toFixed(1) }}%)
              </span>
            </p>
          </div>
          <div class="border-b-2" v-else-if="assessmentResult">
            <p class="font-bold py-1">Your Score:</p>
            <p class="py-2">
              <span class="text-gray-600 font-bold">
                Not attempted
              </span>
            </p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Examiner:</p>
            <p class="py-2">{{ assessment.examiner?.first_name }} {{ assessment.examiner?.last_name }}</p>
          </div>
          <div class="border-b-2">
            <p class="font-bold py-1">Basis:</p>
            <p class="py-2">
              <span v-for="(document, index) in assessment.documents" :key="document.document_id">
                <a href="javascript:void(0)" @click="redirectToDocument(document.document_id)"
                  class="text-blue-500 hover:underline">
                  {{ document.document_name }}
                </a>
                <span v-if="index < assessment.documents.length - 1">, </span>
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="flex justify-end p-10 space-x-4">
      <!-- Take CAT Button - User hasn't attempted assessment -->
      <button v-if="showTakeCat" @click="handleTakeCatModal()"
        class="px-6 py-3 bg-custom-yellow text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors">
        Take C.A.T
      </button>
      
      <!-- Retake CAT Button - User failed -->
      <button v-if="showRetakeCat && !showTakeCat && !showViewResults" @click="handleRetakeCat()"
        class="px-6 py-3 bg-orange-600 text-white rounded-lg font-medium hover:bg-orange-700 transition-colors">
        Retake C.A.T
      </button>
      
      <!-- Pass Summary Button - User passed -->
      <button v-if="showViewResults && !showTakeCat && !showRetakeCat" @click="handleViewResults()"
        class="px-6 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors">
        Pass Summary
      </button>
      
      <!-- Review Test Button - Available for both passed and failed -->
      <button v-if="showReviewTest" @click="handleReviewAssesment()"
        class="px-6 py-3 bg-gray-600 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors">
        Review Test
      </button>
    </div>

    <div v-if="showCatModal"
      class="fixed inset-0 bg-gray-800 bg-opacity-70 overflow-y-auto h-full w-full flex items-center justify-center">
      <div class="bg-white p-8 rounded-lg shadow-xl w-4/10 mt-4 mb-4">
        <h2 class="text-2xl text-gray-700 font-bold mb-4">{{ isRetakeAttempt ? 'Retake Test' : 'Start Test' }}</h2>
        <hr class="h-px my-4 bg-orange-200 border-0 dark:bg-orange-200">
        <div class="mb-6">
          <div class="py-2 px-4 mb-4 border-l-4 text-custom-yellow border-custom-yellow dark:bg-orange-50 rounded-sm"
            role="alert">
            <div class="flex items-center">
              <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <span class="sr-only">warning</span>
              <h3 class="text-lg px-2 font-medium text-gray-800">Reminder!</h3>
            </div>
            <div class="mb-3 px-8 text-sm text-gray-800">
              Kindly make sure you have stable internet and power before starting the test.<br>
              Kindly note that once you start a test, the timer starts running for the allocated period of time.<br>
              Kindly note that once the allocated duration of time elapses, your assessment will be automatically
              submitted
            </div>
          </div>
          <hr class="h-px my-4 bg-orange-200 border-0 dark:bg-orange-200">
        </div>
        <div class="flex justify-between space-x-2">
          <button type="button" @click="closeTakeCatModal"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded">Cancel</button>
          <button @click="handleStartTest(assessment.assessment_id)"
            class="px-4 py-2 bg-custom-yellow text-white rounded">Start Now</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AlertLayout from '@/components/AlertLayout.vue';
import api from '@/services/api';
import SweetAlertService from '@/services/SweetAlertService';

export default {
  name: "staffAssessmentDetailsPage",
  components: {
    AlertLayout
  },
  data() {
    return {
      assessment: [],
      assessment_id: null,
      showCatModal: false,
      assessmentDone: false,
      assessmentResult: null,
      passmarkMet: false,
      isRetakeAttempt: false,
    }
  },
  created() {
    this.assessment_id = this.$route.params.assessment_id;
    this.fetchAssessment();
    this.fetchAssessmentResult();
  },
  computed: {
    // Single computed property to determine the exact state
    assessmentState() {
      if (!this.assessmentDone && (!this.assessmentResult || this.assessmentResult.score === null)) {
        return 'not_attempted';
      }
      // Only evaluate pass/fail if assessment is done
      const passmark = Boolean(this.passmarkMet);
      if (passmark === true) {
        return 'passed';
      }
      if (passmark === false) {
        return 'failed';
      }
      return 'not_attempted';
    },
    
    // Computed properties for cleaner button logic
    showTakeCat() {
      return this.assessmentState === 'not_attempted';
    },
    showRetakeCat() {
      return this.assessmentState === 'failed';
    },
    showViewResults() {
      return this.assessmentState === 'passed';
    },
    showReviewTest() {
      return this.assessmentDone && this.assessmentResult && this.assessmentResult.score !== null;
    },
    isPassed() {
      if (!this.assessmentResult || !this.assessment || 
          this.assessmentResult.score === null || 
          !this.assessment.number_of_questions ||
          this.assessment.number_of_questions === 0) {
        return false;
      }
      
      const score = parseInt(this.assessmentResult.score) || 0;
      const totalQuestions = parseInt(this.assessment.number_of_questions) || 0;
      const passmark = parseFloat(this.assessment.passmark) || 50;
      
      if (totalQuestions === 0) return false;
      
      const percentage = (score / totalQuestions) * 100;
      return percentage >= passmark;
    },
  },
  methods: {
    user() {
      return this.$store.getters["auth/user"];
    },

    getUserAssessmentStatus() {
      // If no assessment result found, show Pending
      if (!this.assessmentResult || !this.assessmentResult.exam_result) {
        return 'Pending';
      }
      
      // If exam_result exists, check passmark_met
      if (this.assessmentResult.passmark_met === true) {
        return 'Complete';
      } else if (this.assessmentResult.passmark_met === false) {
        return 'Ongoing';
      }
      
      return 'Pending';
    },

    getStatusClass() {
      if (!this.assessmentResult || !this.assessmentResult.exam_result) {
        // Pending - gray
        return 'bg-gray-100 text-red-500';
      }
      
      if (this.assessmentResult.passmark_met === true) {
        // Complete - green
        return 'bg-green-100 text-green-800';
      } else if (this.assessmentResult.passmark_met === false) {
        // Ongoing - yellow/orange
        return 'bg-yellow-100 text-yellow-800';
      }
      
      // Default fallback - gray
      return 'bg-gray-100 text-gray-800';
    },

    async fetchAssessment() {
      try {
        const response = await api.getAssessment(this.assessment_id);
        this.assessment = response.data || response;
      } catch (error) {
        this.error = error.message || "Failed to fetch assessments";
      }
    },

    async fetchAssessmentResult() {
      try {
        // Use the original API method that returns the assessment result
        const response = await api.getUserAssessmentResult(this.assessment_id);
        
        // Handle both new (null values) and attempted assessment responses
        if (response && response.assessment_result) {
          const result = response.assessment_result;
          
          // Check if assessment has been attempted by verifying exam_result and user_assessment_detail exist
          if (result.exam_result) {
            // Assessment has been attempted
            const passmarkMetValue = result.passmark_met;
            
            this.assessmentResult = {
              ...result,
              score: result.exam_result?.score || 0
            };
            this.assessmentDone = true;
            this.passmarkMet = Boolean(passmarkMetValue);
          } else {
            // New assessment - no exam_result or user_assessment_detail
            this.assessmentResult = {
              ...result,
              score: null
            };
            this.assessmentDone = false;
            this.passmarkMet = false;
          }
        } else {
          // No result found at all, user hasn't attempted yet
          this.assessmentResult = null;
          this.assessmentDone = false;
          this.passmarkMet = false;
        }

      } catch (error) {
        // If no result found or error, user hasn't attempted yet
        this.assessmentResult = null;
        this.assessmentDone = false;
        this.passmarkMet = false;
      }
    },

    redirectToDocument(documentId) {
      this.$router.push({ name: 'StaffDocumentDetailsPage', params: { document_id: documentId } });
    },

    handleStartTest(assessmentId) {
      // Store retake status before closing modal
      const isRetake = this.isRetakeAttempt;
      
      // Close modal first
      this.showCatModal = false;
      this.isRetakeAttempt = false;
      
      // Navigate to test page with retake parameter if it's a retake
      if (isRetake) {
        this.$router.push({ 
          name: 'StaffAssessmentTestPage', 
          params: { assessment_id: assessmentId },
          query: { retake: 'true' }
        });
      } else {
        this.$router.push({ name: 'StaffAssessmentTestPage', params: { assessment_id: assessmentId } });
      }
    },

    handleTakeCatModal() {
      this.isRetakeAttempt = false;
      this.showCatModal = true
    },

    handleViewResults() {
      this.$router.push({ name: 'AssessmentResultPage', params: { assessment_id: this.assessment_id } });
    },

    async handleRetakeCat() {
      try {
        // Start retake exam
        await api.createRetakeExam(this.assessment_id);
        
        // Show the Start Test reminder modal instead of SweetAlert
        this.isRetakeAttempt = true;
        this.showCatModal = true;
      } catch (error) {
        console.error('Failed to start retake:', error);
        
        // Check if error is about already passed assessment
        if (error.message && error.message.includes('already passed') ||
            error.errors && error.errors.some(err => err.includes('already passed'))) {
          // Navigate to results page instead of showing error
          this.$router.push({
            name: 'AssessmentResultPage',
            params: { assessment_id: this.assessment_id }
          });
        } else {
          SweetAlertService.showError('Error', error.message || 'Failed to start retake assessment');
        }
      }
    },

    handleReviewAssesment() {
      this.$router.push({ name: 'ExamTestReviewPage', params: { assessment_id: this.assessment_id } });
    },

    closeTakeCatModal() {
      this.showCatModal = false;
    },

    formatDuration(duration) {
      if (!duration) return 'N/A';
      
      // Handle ISO 8601 duration format like "2000-01-02T00:30:00.000+03:00"
      try {
        // Extract time part from the ISO string
        const timeMatch = duration.match(/T(\d{2}):(\d{2}):(\d{2})/);
        if (timeMatch) {
          const hours = parseInt(timeMatch[1]);
          const minutes = parseInt(timeMatch[2]);
          const seconds = parseInt(timeMatch[3]);
          
          if (hours > 0) {
            return `${hours}h ${minutes}m`;
          } else if (minutes > 0) {
            return `${minutes} minutes`;
          } else {
            return `${seconds} seconds`;
          }
        }
        
        // Fallback: try to parse as duration string directly
        if (duration.includes('H') || duration.includes('M') || duration.includes('S')) {
          const hourMatch = duration.match(/(\d+)H/);
          const minuteMatch = duration.match(/(\d+)M/);
          const secondMatch = duration.match(/(\d+)S/);
          
          let result = [];
          if (hourMatch) result.push(`${hourMatch[1]}h`);
          if (minuteMatch) result.push(`${minuteMatch[1]}m`);
          if (secondMatch && !minuteMatch) result.push(`${secondMatch[1]}s`);
          
          return result.length > 0 ? result.join(' ') : '0 minutes';
        }
        
        // Fallback: return original if no pattern matches
        return duration;
      } catch (error) {
        console.error('Error formatting duration:', error);
        return duration;
      }
    },
  },
};
</script>

<style scoped></style>
